import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

/**
 * Custom React hook to check if ISPM MFE (Microfrontend) is enabled.
 * 
 * **⚠️ WARNING: This hook should ONLY be used within MFE registration routes.**
 * **Do not use this hook in regular components as it depends on specific initialization timing.**
 * 
 * ## How it works:
 * 1. Checks if ISPM services are enabled via Redux state (`ISPM_SERVICES_ENABLED`)
 * 2. If enabled, waits for `window.envConfig.ISPM_MFE_ENABLED` to be populated by external script
 * 3. The external script `checkIspmFeatureFlag.js` calls the ISPM API and caches the result
 * 4. This hook polls for that cached result with a 5-second timeout
 * 
 * ## Dependencies:
 * - **Redux State**: Requires `state.user.domainInfo.ISPM_SERVICES_ENABLED` to be available
 * - **External Script**: Depends on `ui-track-singlespa/dev/public/ECMv6/assets/js/mfe/checkIspmFeatureFlag.js`
 *   which populates `window.envConfig.ISPM_MFE_ENABLED` by calling `/ECMv6/api/ispm/v1/config`
 * - **Timing**: Must be used after the external script has had time to execute
 * 
 * ## Usage Example:
 * ```javascript
 * // ✅ CORRECT - In MFE route component
 * const RenderIspm = ({ location }) => {
 *   const { isMfeEnabled, isLoading } = useIspmFeatureFlag();
 *   
 *   if (isLoading) return <AppLoading />;
 *   return isMfeEnabled ? <ISPMComponent /> : <NotFound />;
 * };
 * 
 * // ❌ INCORRECT - In regular component
 * const MyComponent = () => {
 *   const { isMfeEnabled } = useIspmFeatureFlag(); // Don't do this!
 *   // ...
 * };
 * ```
 * 
 * ## Return Values:
 * @returns {Object} Hook state object
 * @returns {boolean} returns.isMfeEnabled - Whether ISPM MFE is enabled (false if services disabled or API returns false)
 * @returns {boolean} returns.isLoading - Whether the feature flag check is in progress
 * @returns {boolean} returns.isError - Whether there was an error checking the feature flag
 * @returns {Error|null} returns.error - The error object if feature flag check failed
 * 
 * ## Behavior:
 * - If `ISPM_SERVICES_ENABLED` is false: Returns `{ isMfeEnabled: false, isLoading: false }` immediately
 * - If `window.envConfig.ISPM_MFE_ENABLED` exists: Returns cached value immediately
 * - Otherwise: Polls every 100ms for up to 5 seconds, then defaults to `false`
 * 
 * ## Error Handling:
 * - Network failures in external script: Defaults to `false`
 * - Timeout after 5 seconds: Defaults to `false`
 * - Component unmount: Cleans up polling to prevent memory leaks
 * 
 * @since 24.10
 * @category MFE
 * @subcategory Hooks
 */
const useIspmFeatureFlag = () => {
  const { ISPM_SERVICES_ENABLED } = useSelector(state => state.user?.domainInfo || {});

  const { data: isMfeEnabled = false, isLoading, error, isError } = useQuery({
    queryKey: ['ispm-feature-flag'],
    queryFn: () => {
      return new Promise((resolve, reject) => {
        if (window.envConfig?.ISPM_MFE_ENABLED !== undefined) {
          resolve(!!window.envConfig.ISPM_MFE_ENABLED);
        } else {
          reject(new Error('EnvConfig not ready'));
        }
      });
    },
    enabled: !!ISPM_SERVICES_ENABLED,
    retry: 8,
    retryDelay: (attemptIndex) => Math.min(100 * Math.pow(2, attemptIndex), 1000),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return { 
    /** @type {boolean} Whether ISPM MFE is enabled and should be rendered */
    isMfeEnabled: !!ISPM_SERVICES_ENABLED ? isMfeEnabled : false, 
    /** @type {boolean} Whether the feature flag check is still in progress */
    isLoading: !!ISPM_SERVICES_ENABLED ? isLoading : false,
    /** @type {boolean} Whether there was an error checking the feature flag */
    isError: !!ISPM_SERVICES_ENABLED ? isError : false,
    /** @type {Error|null} The error object if feature flag check failed */
    error: !!ISPM_SERVICES_ENABLED ? error : null
  };
};

export default useIspmFeatureFlag;